import advectShader from "./shaders/advect.wgsl?raw";
import boundaryShader from "./shaders/boundary.wgsl?raw";
import clearPressureShader from "./shaders/clear-pressure.wgsl?raw";
import copyShader from "./shaders/copy.wgsl?raw";
import curlShader from "./shaders/curl.wgsl?raw";
import diffuseShader from "./shaders/diffuse.wgsl?raw";
import divergenceShader from "./shaders/divergence.wgsl?raw";
import gradientShader from "./shaders/gradient.wgsl?raw";
import impulseShader from "./shaders/impulse.wgsl?raw";
import pressureShader from "./shaders/pressure.wgsl?raw";
import renderShader from "./shaders/render.wgsl?raw";
import vorticityShader from "./shaders/vorticity.wgsl?raw";
import {
  DEFAULT_SOLVER_SETTINGS,
  type FluidImpulse,
  type ObstacleMask,
  type SimulationSize,
  type SolverSettings,
} from "./types";

interface ComputePipelines {
  advect: GPUComputePipeline;
  boundary: GPUComputePipeline;
  clearPressure: GPUComputePipeline;
  copy: GPUComputePipeline;
  curl: GPUComputePipeline;
  diffuse: GPUComputePipeline;
  vorticity: GPUComputePipeline;
  divergence: GPUComputePipeline;
  pressure: GPUComputePipeline;
  gradient: GPUComputePipeline;
  impulse: GPUComputePipeline;
}

interface TextureHandle {
  texture: GPUTexture;
  view: GPUTextureView;
}

class TexturePair {
  private index = 0;

  constructor(
    readonly first: TextureHandle,
    readonly second: TextureHandle,
  ) {}

  get read(): TextureHandle {
    return this.index === 0 ? this.first : this.second;
  }

  get write(): TextureHandle {
    return this.index === 0 ? this.second : this.first;
  }

  swap(): void {
    this.index = 1 - this.index;
  }

  destroy(): void {
    this.first.texture.destroy();
    this.second.texture.destroy();
  }
}

interface FluidResources {
  velocity: TexturePair;
  dye: TexturePair;
  pressure: TexturePair;
  divergence: TextureHandle;
  curl: TextureHandle;
  diffusionSource: TextureHandle;
  obstacle: TextureHandle;
}

export type DeviceLostHandler = (message: string) => void;

export class FluidSolver {
  readonly device: GPUDevice;
  readonly size: SimulationSize = { width: 1, height: 1 };

  private readonly context: GPUCanvasContext;
  private readonly pipelines: ComputePipelines;
  private readonly renderPipeline: GPURenderPipeline;
  private readonly simUniformBuffer: GPUBuffer;
  private readonly velocityAdvectBuffer: GPUBuffer;
  private readonly dyeAdvectBuffer: GPUBuffer;
  private readonly diffusionBuffer: GPUBuffer;
  private readonly impulseBuffers: GPUBuffer[] = [];
  private readonly settings: SolverSettings;
  private resources: FluidResources | undefined;
  private elapsed = 0;
  private inletSpeedScale = 1;

  private constructor(
    device: GPUDevice,
    context: GPUCanvasContext,
    canvasFormat: GPUTextureFormat,
    settings: SolverSettings,
    onDeviceLost: DeviceLostHandler,
  ) {
    this.device = device;
    this.context = context;
    this.settings = settings;

    this.simUniformBuffer = device.createBuffer({
      label: "simulation uniforms",
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.velocityAdvectBuffer = this.createAdvectBuffer(
      settings.velocityDissipation,
      "velocity advection uniforms",
    );
    this.dyeAdvectBuffer = this.createAdvectBuffer(
      settings.dyeDissipation,
      "dye advection uniforms",
    );
    this.diffusionBuffer = this.createDiffusionBuffer(
      settings.kinematicViscosity,
    );

    this.pipelines = {
      advect: this.createComputePipeline("advect", advectShader),
      boundary: this.createComputePipeline("boundary", boundaryShader),
      clearPressure: this.createComputePipeline(
        "clear pressure",
        clearPressureShader,
      ),
      copy: this.createComputePipeline("copy", copyShader),
      curl: this.createComputePipeline("curl", curlShader),
      diffuse: this.createComputePipeline("diffuse", diffuseShader),
      vorticity: this.createComputePipeline(
        "vorticity",
        vorticityShader,
        vorticityLayout(),
      ),
      divergence: this.createComputePipeline("divergence", divergenceShader),
      pressure: this.createComputePipeline(
        "pressure",
        pressureShader,
        pressureLayout(),
      ),
      gradient: this.createComputePipeline(
        "gradient",
        gradientShader,
        gradientLayout(),
      ),
      impulse: this.createComputePipeline("impulse", impulseShader),
    };

    const renderModule = device.createShaderModule({
      label: "fluid presentation shader",
      code: renderShader,
    });
    this.renderPipeline = device.createRenderPipeline({
      label: "fluid presentation pipeline",
      layout: "auto",
      vertex: {
        module: renderModule,
        entryPoint: "vertexMain",
      },
      fragment: {
        module: renderModule,
        entryPoint: "fragmentMain",
        targets: [{ format: canvasFormat }],
      },
      primitive: { topology: "triangle-list" },
    });

    device.addEventListener("uncapturederror", (event) => {
      onDeviceLost(`WebGPU error: ${event.error.message}`);
    });
    void device.lost.then((info) => {
      onDeviceLost(
        info.message || `The WebGPU device was lost (${info.reason}).`,
      );
    });
  }

  static async create(
    canvas: HTMLCanvasElement,
    onDeviceLost: DeviceLostHandler,
    overrides: Partial<SolverSettings> = {},
  ): Promise<FluidSolver> {
    if (!navigator.gpu) {
      throw new Error(
        "WebGPU is not available in this browser. Use a current WebGPU-capable browser.",
      );
    }

    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: "high-performance",
    });
    if (!adapter) {
      throw new Error("No compatible WebGPU adapter was found.");
    }

    const device = await adapter.requestDevice();
    const context = canvas.getContext("webgpu");
    if (!context) {
      throw new Error("The WebGPU canvas context could not be created.");
    }
    const canvasFormat = navigator.gpu.getPreferredCanvasFormat();
    context.configure({
      device,
      format: canvasFormat,
      alphaMode: "opaque",
    });

    device.pushErrorScope("validation");
    const solver = new FluidSolver(
      device,
      context,
      canvasFormat,
      { ...DEFAULT_SOLVER_SETTINGS, ...overrides },
      onDeviceLost,
    );
    const initializationError = await device.popErrorScope();
    if (initializationError) {
      solver.destroy();
      throw new Error(
        `WebGPU pipeline initialization failed: ${initializationError.message}`,
      );
    }
    return solver;
  }

  resize(size: SimulationSize): void {
    if (
      this.resources &&
      this.size.width === size.width &&
      this.size.height === size.height
    ) {
      return;
    }
    this.destroyResources();
    this.size.width = size.width;
    this.size.height = size.height;
    this.resources = this.createResources(size);
    this.updateSimUniforms(1 / 60, this.elapsed);
  }

  setInletSpeedScale(scale: number): void {
    this.inletSpeedScale = Math.max(0, scale);
    this.updateSimUniforms(0, this.elapsed);
  }

  step(
    deltaTime: number,
    elapsed: number,
    impulses: readonly FluidImpulse[],
  ): void {
    const resources = this.requireResources();
    const dt = Math.min(1 / 30, Math.max(1 / 240, deltaTime));
    this.elapsed = elapsed;
    this.updateSimUniforms(dt, elapsed);

    const encoder = this.device.createCommandEncoder({
      label: "fluid simulation frame",
    });
    const pass = encoder.beginComputePass({
      label: "fluid compute passes",
    });

    const activeImpulses = impulses.slice(0, 12);
    activeImpulses.forEach((impulse, index) => {
      this.encodeImpulse(pass, resources, impulse, index);
    });
    this.encodeAdvect(
      pass,
      resources,
      resources.velocity,
      resources.velocity.read,
      this.velocityAdvectBuffer,
    );
    this.encodeCurl(pass, resources);
    this.encodeVorticity(pass, resources);
    this.encodeBoundary(pass, resources);
    this.encodeDiffusion(pass, resources);
    this.encodeBoundary(pass, resources);
    this.encodeProjection(pass, resources, this.settings.pressureIterations);
    this.encodeBoundary(pass, resources);
    this.encodeAdvect(
      pass,
      resources,
      resources.dye,
      resources.velocity.read,
      this.dyeAdvectBuffer,
    );
    pass.end();

    this.encodeRender(encoder, resources);
    this.device.queue.submit([encoder.finish()]);
  }

  render(): void {
    const resources = this.requireResources();
    const encoder = this.device.createCommandEncoder({
      label: "fluid presentation only",
    });
    this.encodeRender(encoder, resources);
    this.device.queue.submit([encoder.finish()]);
  }

  async setObstacle(mask: ObstacleMask): Promise<void> {
    const resources = this.requireResources();
    if (mask.width !== this.size.width || mask.height !== this.size.height) {
      throw new Error("Obstacle dimensions must match the simulation grid.");
    }

    this.uploadObstacle(resources.obstacle.texture, mask);
    this.updateSimUniforms(0, this.elapsed);

    const encoder = this.device.createCommandEncoder({
      label: "obstacle rebuild projection",
    });
    const pass = encoder.beginComputePass({
      label: "obstacle stabilization",
    });
    this.encodeClearPressure(pass, resources);
    this.encodeBoundary(pass, resources);
    this.encodeProjection(
      pass,
      resources,
      Math.max(36, this.settings.pressureIterations),
    );
    this.encodeBoundary(pass, resources);
    pass.end();
    this.encodeRender(encoder, resources);
    this.device.queue.submit([encoder.finish()]);
    await this.device.queue.onSubmittedWorkDone();
  }

  destroy(): void {
    this.destroyResources();
    this.simUniformBuffer.destroy();
    this.velocityAdvectBuffer.destroy();
    this.dyeAdvectBuffer.destroy();
    this.diffusionBuffer.destroy();
    for (const buffer of this.impulseBuffers) {
      buffer.destroy();
    }
  }

  private createResources(size: SimulationSize): FluidResources {
    const simulationUsage =
      GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING;
    const singleUsage =
      GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.STORAGE_BINDING;

    return {
      velocity: this.createTexturePair(
        size,
        "rgba16float",
        simulationUsage,
        "velocity",
      ),
      dye: this.createTexturePair(
        size,
        "rgba16float",
        simulationUsage,
        "dye",
      ),
      pressure: this.createTexturePair(
        size,
        "r32float",
        simulationUsage,
        "pressure",
      ),
      divergence: this.createTexture(
        size,
        "r32float",
        singleUsage,
        "divergence",
      ),
      curl: this.createTexture(size, "r32float", singleUsage, "curl"),
      diffusionSource: this.createTexture(
        size,
        "rgba16float",
        simulationUsage,
        "diffusion source velocity",
      ),
      obstacle: this.createTexture(
        size,
        "r8unorm",
        GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
        "text obstacle",
      ),
    };
  }

  private createTexturePair(
    size: SimulationSize,
    format: GPUTextureFormat,
    usage: GPUTextureUsageFlags,
    label: string,
  ): TexturePair {
    return new TexturePair(
      this.createTexture(size, format, usage, `${label} A`),
      this.createTexture(size, format, usage, `${label} B`),
    );
  }

  private createTexture(
    size: SimulationSize,
    format: GPUTextureFormat,
    usage: GPUTextureUsageFlags,
    label: string,
  ): TextureHandle {
    const texture = this.device.createTexture({
      label,
      size: [size.width, size.height],
      format,
      usage,
    });
    return { texture, view: texture.createView() };
  }

  private createComputePipeline(
    label: string,
    code: string,
    layoutEntries?: GPUBindGroupLayoutEntry[],
  ): GPUComputePipeline {
    const layout = layoutEntries
      ? this.device.createPipelineLayout({
          label: `${label} pipeline layout`,
          bindGroupLayouts: [
            this.device.createBindGroupLayout({
              label: `${label} bind group layout`,
              entries: layoutEntries,
            }),
          ],
        })
      : "auto";
    return this.device.createComputePipeline({
      label: `${label} pipeline`,
      layout,
      compute: {
        module: this.device.createShaderModule({
          label: `${label} shader`,
          code,
        }),
        entryPoint: "main",
      },
    });
  }

  private createAdvectBuffer(value: number, label: string): GPUBuffer {
    const buffer = this.device.createBuffer({
      label,
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(buffer, 0, new Float32Array([value, 0, 0, 0]));
    return buffer;
  }

  private createDiffusionBuffer(kinematicViscosity: number): GPUBuffer {
    const buffer = this.device.createBuffer({
      label: "velocity diffusion uniforms",
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(
      buffer,
      0,
      new Float32Array([kinematicViscosity, 0, 0, 0]),
    );
    return buffer;
  }

  private encodeImpulse(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
    impulse: FluidImpulse,
    bufferIndex: number,
  ): void {
    const buffer = this.getImpulseBuffer(bufferIndex);
    this.device.queue.writeBuffer(
      buffer,
      0,
      new Float32Array([
        impulse.point[0],
        impulse.point[1],
        impulse.velocity[0],
        impulse.velocity[1],
        impulse.radius,
        impulse.dyeAmount,
        0,
        0,
        impulse.color[0],
        impulse.color[1],
        impulse.color[2],
        1,
      ]),
    );

    this.dispatch(pass, this.pipelines.impulse, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: { buffer } },
      { binding: 2, resource: resources.velocity.read.view },
      { binding: 3, resource: resources.dye.read.view },
      { binding: 4, resource: resources.obstacle.view },
      { binding: 5, resource: resources.velocity.write.view },
      { binding: 6, resource: resources.dye.write.view },
    ]);
    resources.velocity.swap();
    resources.dye.swap();
  }

  private encodeAdvect(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
    target: TexturePair,
    velocity: TextureHandle,
    uniformBuffer: GPUBuffer,
  ): void {
    this.dispatch(pass, this.pipelines.advect, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: { buffer: uniformBuffer } },
      { binding: 2, resource: target.read.view },
      { binding: 3, resource: velocity.view },
      { binding: 4, resource: resources.obstacle.view },
      { binding: 5, resource: target.write.view },
    ]);
    target.swap();
  }

  private encodeCurl(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
  ): void {
    this.dispatch(pass, this.pipelines.curl, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: resources.velocity.read.view },
      { binding: 2, resource: resources.obstacle.view },
      { binding: 3, resource: resources.curl.view },
    ]);
  }

  private encodeVorticity(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
  ): void {
    this.dispatch(pass, this.pipelines.vorticity, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: resources.velocity.read.view },
      { binding: 2, resource: resources.curl.view },
      { binding: 3, resource: resources.obstacle.view },
      { binding: 4, resource: resources.velocity.write.view },
    ]);
    resources.velocity.swap();
  }

  private encodeBoundary(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
  ): void {
    this.dispatch(pass, this.pipelines.boundary, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: resources.velocity.read.view },
      { binding: 2, resource: resources.obstacle.view },
      { binding: 3, resource: resources.velocity.write.view },
    ]);
    resources.velocity.swap();
  }

  private encodeClearPressure(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
  ): void {
    this.dispatch(pass, this.pipelines.clearPressure, [
      { binding: 0, resource: resources.pressure.first.view },
    ]);
    this.dispatch(pass, this.pipelines.clearPressure, [
      { binding: 0, resource: resources.pressure.second.view },
    ]);
  }

  private encodeDiffusion(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
  ): void {
    if (
      this.settings.kinematicViscosity <= 0 ||
      this.settings.viscosityIterations <= 0
    ) {
      return;
    }

    this.dispatch(pass, this.pipelines.copy, [
      { binding: 0, resource: resources.velocity.read.view },
      { binding: 1, resource: resources.diffusionSource.view },
    ]);

    for (
      let iteration = 0;
      iteration < this.settings.viscosityIterations;
      iteration += 1
    ) {
      this.dispatch(pass, this.pipelines.diffuse, [
        { binding: 0, resource: { buffer: this.simUniformBuffer } },
        { binding: 1, resource: { buffer: this.diffusionBuffer } },
        { binding: 2, resource: resources.diffusionSource.view },
        { binding: 3, resource: resources.velocity.read.view },
        { binding: 4, resource: resources.obstacle.view },
        { binding: 5, resource: resources.velocity.write.view },
      ]);
      resources.velocity.swap();
    }
  }

  private encodeProjection(
    pass: GPUComputePassEncoder,
    resources: FluidResources,
    iterations: number,
  ): void {
    this.dispatch(pass, this.pipelines.divergence, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: resources.velocity.read.view },
      { binding: 2, resource: resources.obstacle.view },
      { binding: 3, resource: resources.divergence.view },
    ]);

    for (let iteration = 0; iteration < iterations; iteration += 1) {
      this.dispatch(pass, this.pipelines.pressure, [
        { binding: 0, resource: { buffer: this.simUniformBuffer } },
        { binding: 1, resource: resources.pressure.read.view },
        { binding: 2, resource: resources.divergence.view },
        { binding: 3, resource: resources.obstacle.view },
        { binding: 4, resource: resources.pressure.write.view },
      ]);
      resources.pressure.swap();
    }

    this.dispatch(pass, this.pipelines.gradient, [
      { binding: 0, resource: { buffer: this.simUniformBuffer } },
      { binding: 1, resource: resources.velocity.read.view },
      { binding: 2, resource: resources.pressure.read.view },
      { binding: 3, resource: resources.obstacle.view },
      { binding: 4, resource: resources.velocity.write.view },
    ]);
    resources.velocity.swap();
  }

  private encodeRender(
    encoder: GPUCommandEncoder,
    resources: FluidResources,
  ): void {
    const pass = encoder.beginRenderPass({
      label: "fluid presentation",
      colorAttachments: [
        {
          view: this.context.getCurrentTexture().createView(),
          clearValue: { r: 0.008, g: 0.01, b: 0.016, a: 1 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });
    pass.setPipeline(this.renderPipeline);
    pass.setBindGroup(
      0,
      this.device.createBindGroup({
        label: "fluid presentation resources",
        layout: this.renderPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: resources.dye.read.view },
          { binding: 1, resource: resources.velocity.read.view },
        ],
      }),
    );
    pass.draw(3);
    pass.end();
  }

  private dispatch(
    pass: GPUComputePassEncoder,
    pipeline: GPUComputePipeline,
    entries: GPUBindGroupEntry[],
  ): void {
    pass.setPipeline(pipeline);
    pass.setBindGroup(
      0,
      this.device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries,
      }),
    );
    pass.dispatchWorkgroups(
      Math.ceil(this.size.width / 8),
      Math.ceil(this.size.height / 8),
    );
  }

  private updateSimUniforms(deltaTime: number, elapsed: number): void {
    this.device.queue.writeBuffer(
      this.simUniformBuffer,
      0,
      new Float32Array([
        this.size.width,
        this.size.height,
        1 / this.size.width,
        1 / this.size.height,
        deltaTime,
        this.settings.curlStrength,
        elapsed,
        this.settings.inletSpeed * this.inletSpeedScale,
      ]),
    );
  }

  private getImpulseBuffer(index: number): GPUBuffer {
    const existing = this.impulseBuffers[index];
    if (existing) {
      return existing;
    }
    const buffer = this.device.createBuffer({
      label: `impulse uniforms ${index}`,
      size: 48,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.impulseBuffers[index] = buffer;
    return buffer;
  }

  private uploadObstacle(texture: GPUTexture, mask: ObstacleMask): void {
    const bytesPerRow = Math.ceil(mask.width / 256) * 256;
    const upload =
      bytesPerRow === mask.width
        ? mask.data
        : copyWithPaddedRows(mask.data, mask.width, mask.height, bytesPerRow);
    this.device.queue.writeTexture(
      { texture },
      upload,
      { bytesPerRow, rowsPerImage: mask.height },
      { width: mask.width, height: mask.height },
    );
  }

  private requireResources(): FluidResources {
    if (!this.resources) {
      throw new Error("FluidSolver.resize() must be called before rendering.");
    }
    return this.resources;
  }

  private destroyResources(): void {
    if (!this.resources) {
      return;
    }
    this.resources.velocity.destroy();
    this.resources.dye.destroy();
    this.resources.pressure.destroy();
    this.resources.divergence.texture.destroy();
    this.resources.curl.texture.destroy();
    this.resources.diffusionSource.texture.destroy();
    this.resources.obstacle.texture.destroy();
    this.resources = undefined;
  }
}

function copyWithPaddedRows(
  source: Uint8Array,
  width: number,
  height: number,
  bytesPerRow: number,
): Uint8Array {
  const result = new Uint8Array(bytesPerRow * height);
  for (let row = 0; row < height; row += 1) {
    result.set(
      source.subarray(row * width, row * width + width),
      row * bytesPerRow,
    );
  }
  return result;
}

const COMPUTE_VISIBILITY: GPUShaderStageFlags = 0x4;

function uniformEntry(binding: number): GPUBindGroupLayoutEntry {
  return {
    binding,
    visibility: COMPUTE_VISIBILITY,
    buffer: { type: "uniform" },
  };
}

function textureEntry(
  binding: number,
  sampleType: GPUTextureSampleType = "float",
): GPUBindGroupLayoutEntry {
  return {
    binding,
    visibility: COMPUTE_VISIBILITY,
    texture: { sampleType, viewDimension: "2d", multisampled: false },
  };
}

function storageEntry(
  binding: number,
  format: GPUTextureFormat,
): GPUBindGroupLayoutEntry {
  return {
    binding,
    visibility: COMPUTE_VISIBILITY,
    storageTexture: {
      access: "write-only",
      format,
      viewDimension: "2d",
    },
  };
}

function vorticityLayout(): GPUBindGroupLayoutEntry[] {
  return [
    uniformEntry(0),
    textureEntry(1),
    textureEntry(2, "unfilterable-float"),
    textureEntry(3),
    storageEntry(4, "rgba16float"),
  ];
}

function pressureLayout(): GPUBindGroupLayoutEntry[] {
  return [
    uniformEntry(0),
    textureEntry(1, "unfilterable-float"),
    textureEntry(2, "unfilterable-float"),
    textureEntry(3),
    storageEntry(4, "r32float"),
  ];
}

function gradientLayout(): GPUBindGroupLayoutEntry[] {
  return [
    uniformEntry(0),
    textureEntry(1),
    textureEntry(2, "unfilterable-float"),
    textureEntry(3),
    storageEntry(4, "rgba16float"),
  ];
}
